import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import DoctorSearchSelect from '../../../components/ui/DoctorSearchSelect'
import TypeaheadSearch, { TypeaheadOption } from '../../../components/ui/TypeaheadSearch'
import Badge from '../../../components/ui/Badge'
import Table, { Pagination } from '../../../components/ui/Table'
import Modal from '../../../components/ui/Modal'
import api from '../../../lib/api'
import { formatCurrency, formatDate } from '../../../lib/formatters'
import type { Payment, Invoice, PaginatedResponse, PaymentMethod } from '../../../types'
import toast from 'react-hot-toast'

const METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  upi: 'UPI',
  bank_transfer: 'Bank Transfer',
  cheque: 'Cheque',
  card: 'Card',
}

export default function PaymentsListPage() {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [method, setMethod] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(!!searchParams.get('new'))
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null)
  const preselectedInvoiceId = searchParams.get('invoiceId')

  const { data, isLoading } = useQuery({
    queryKey: ['payments', page, search, method],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '10' })
      if (search) params.append('search', search)
      if (method) params.append('method', method)
      const { data } = await api.get<PaginatedResponse<Payment>>(`/payments?${params}`)
      return data
    },
  })

  const getMethodBadge = (paymentMethod: PaymentMethod) => {
    const variants: Record<PaymentMethod, 'success' | 'info' | 'warning' | 'gray'> = {
      cash: 'success',
      upi: 'info',
      bank_transfer: 'info',
      cheque: 'warning',
      card: 'info',
    }
    return <Badge variant={variants[paymentMethod]}>{METHOD_LABELS[paymentMethod]}</Badge>
  }

  const columns = [
    {
      key: 'displayId',
      header: 'Receipt #',
      render: (payment: Payment) => (
        <span className="font-medium text-gray-900 dark:text-white">{payment.displayId}</span>
      ),
    },
    {
      key: 'doctor',
      header: 'Doctor / Clinic',
      render: (payment: Payment) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{payment.doctor?.name ?? '-'}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{payment.doctor?.clinicName}</p>
        </div>
      ),
    },
    {
      key: 'invoice',
      header: 'Invoice',
      render: (payment: Payment) => (
        payment.invoice ? (
          <Link to={`/invoices/${payment.invoiceId}`} className="text-primary-600 hover:underline">
            {payment.invoice.displayId}
          </Link>
        ) : (
          <span className="text-gray-500">Advance</span>
        )
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (payment: Payment) => (
        <span className="font-semibold text-green-600">{formatCurrency(payment.amount)}</span>
      ),
    },
    {
      key: 'method',
      header: 'Method',
      render: (payment: Payment) => getMethodBadge(payment.method),
    },
    {
      key: 'reference',
      header: 'Reference',
      render: (payment: Payment) => (
        <span className="text-sm text-gray-600 dark:text-gray-300 font-mono">
          {payment.referenceNumber || '-'}
        </span>
      ),
    },
    {
      key: 'receivedAt',
      header: 'Received On',
      render: (payment: Payment) => (
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {formatDate(payment.receivedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (payment: Payment) => (
        <button
          onClick={() => setEditingPayment(payment)}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 p-1"
          title="Edit Payment"
        >
          <i className="fas fa-edit"></i>
        </button>
      ),
    },
  ]

  const totalReceived = data?.data?.reduce((sum, p) => sum + p.amount, 0) ?? 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Payments</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track all payment receipts
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <i className="fas fa-plus mr-2"></i>
          Record Payment
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">Total Collected (Page)</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(totalReceived)}</p>
            </div>
            <i className="fas fa-indian-rupee-sign text-3xl text-green-200"></i>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">This Month</p>
              <p className="text-2xl font-bold mt-1">{data?.data?.length ?? 0} receipts</p>
            </div>
            <i className="fas fa-receipt text-3xl text-blue-200"></i>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100">Most Used Method</p>
              <p className="text-2xl font-bold mt-1">UPI</p>
            </div>
            <i className="fas fa-wallet text-3xl text-purple-200"></i>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <TypeaheadSearch
              placeholder="Search by receipt #, doctor..."
              value={search}
              onChange={(value) => { setSearch(value); setPage(1) }}
              onSelect={(option) => { setSearch(option.label); setPage(1) }}
              queryKey={['payments', 'search']}
              queryFn={async () => {
                try {
                  const { data } = await api.get<PaginatedResponse<Payment>>('/payments?limit=100')
                  return (data.data || []).map((payment: Payment): TypeaheadOption => ({
                    id: payment.id,
                    label: payment.displayId,
                    sublabel: `${payment.doctor?.name || 'Unknown'} • ${formatCurrency(payment.amount)}`,
                    icon: 'fa-receipt',
                  }))
                } catch {
                  return []
                }
              }}
              maxResults={8}
            />
          </div>
          <SearchableSelect
            value={method}
            onChange={(val) => { setMethod(val); setPage(1) }}
            placeholder="All Methods"
            searchable={false}
            options={[
              { value: '', label: 'All Methods' },
              { value: 'cash', label: 'Cash' },
              { value: 'upi', label: 'UPI' },
              { value: 'bank_transfer', label: 'Bank Transfer' },
              { value: 'cheque', label: 'Cheque' },
              { value: 'card', label: 'Card' },
            ]}
          />
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={data?.data ?? []}
          loading={isLoading}
          keyExtractor={(payment) => payment.id}
          emptyMessage="No payments found"
        />
        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Create Modal */}
      <CreatePaymentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false)
          queryClient.invalidateQueries({ queryKey: ['payments'] })
          queryClient.invalidateQueries({ queryKey: ['invoices'] })
        }}
        preselectedInvoiceId={preselectedInvoiceId}
      />

      {/* Edit Modal */}
      {editingPayment && (
        <EditPaymentModal
          payment={editingPayment}
          isOpen={!!editingPayment}
          onClose={() => setEditingPayment(null)}
          onSuccess={() => {
            setEditingPayment(null)
            queryClient.invalidateQueries({ queryKey: ['payments'] })
            queryClient.invalidateQueries({ queryKey: ['invoices'] })
            queryClient.invalidateQueries({ queryKey: ['reports'] })
            queryClient.invalidateQueries({ queryKey: ['statements'] })
          }}
        />
      )}
    </div>
  )
}

interface CreatePaymentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  preselectedInvoiceId?: string | null
}

function CreatePaymentModal({ isOpen, onClose, onSuccess, preselectedInvoiceId }: CreatePaymentModalProps) {
  const [formData, setFormData] = useState({
    doctorId: '',
    selectedInvoiceIds: preselectedInvoiceId ? [preselectedInvoiceId] : [] as string[],
    amount: '',
    method: 'upi' as PaymentMethod,
    referenceNumber: '',
    notes: '',
    receivedAt: new Date().toISOString().split('T')[0],
  })
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [submitAttempted, setSubmitAttempted] = useState(false)

  // Fetch invoices with outstanding balance only
  const { data: invoices } = useQuery({
    queryKey: ['invoices', 'unpaid', formData.doctorId],
    queryFn: async () => {
      if (!formData.doctorId) return []
      const { data } = await api.get<PaginatedResponse<Invoice>>(
        `/invoices?doctorId=${formData.doctorId}&status=sent,partial&limit=100`
      )
      // Filter to only show invoices with balance > 0
      return (data.data || []).filter((inv: Invoice) => inv.balanceAmount > 0)
    },
    enabled: !!formData.doctorId,
  })

  const resetForm = () => {
    setFormData({
      doctorId: '',
      selectedInvoiceIds: preselectedInvoiceId ? [preselectedInvoiceId] : [],
      amount: '',
      method: 'upi' as PaymentMethod,
      referenceNumber: '',
      notes: '',
      receivedAt: new Date().toISOString().split('T')[0],
    })
    setTouched({})
    setSubmitAttempted(false)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  // Calculate totals for selected invoices
  const selectedInvoices = invoices?.filter(inv => formData.selectedInvoiceIds.includes(inv.id)) || []
  const totalOutstanding = selectedInvoices.reduce((sum, inv) => sum + inv.balanceAmount, 0)
  const paymentAmount = parseFloat(formData.amount) || 0
  const excessAmount = Math.max(0, paymentAmount - totalOutstanding)

  // Toggle invoice selection
  const toggleInvoiceSelection = (invoiceId: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedInvoiceIds.includes(invoiceId)
      const newIds = isSelected
        ? prev.selectedInvoiceIds.filter(id => id !== invoiceId)
        : [...prev.selectedInvoiceIds, invoiceId]
      return { ...prev, selectedInvoiceIds: newIds }
    })
    setTouched({ ...touched, selectedInvoiceIds: true })
  }

  // Select all invoices
  const selectAllInvoices = () => {
    if (invoices) {
      setFormData(prev => ({ ...prev, selectedInvoiceIds: invoices.map(inv => inv.id) }))
    }
  }

  // Auto-fill amount with total outstanding
  const autoFillAmount = () => {
    setFormData(prev => ({ ...prev, amount: totalOutstanding.toFixed(2) }))
  }

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return api.post('/payments', {
        doctorId: data.doctorId,
        invoiceIds: data.selectedInvoiceIds,
        amount: parseFloat(data.amount),
        method: data.method,
        referenceNumber: data.referenceNumber || undefined,
        notes: data.notes || undefined,
        receivedAt: new Date(data.receivedAt).toISOString(),
      })
    },
    onSuccess: (response) => {
      resetForm()
      onSuccess()
      // Show allocation info if available
      const resData = response.data as { credit?: { amount: number } }
      if (resData?.credit?.amount) {
        // Toast is handled by onSuccess callback
      }
    },
  })

  // Validation
  const errors: Record<string, string> = {}
  if (!formData.doctorId) errors.doctorId = 'Doctor is required'
  if (formData.selectedInvoiceIds.length === 0) errors.selectedInvoiceIds = 'At least one invoice must be selected'
  if (!formData.amount || parseFloat(formData.amount) <= 0) errors.amount = 'Valid amount is required'
  if (!formData.receivedAt) errors.receivedAt = 'Date is required'

  const showError = (field: string) => (submitAttempted || touched[field]) && errors[field]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitAttempted(true)
    
    if (Object.keys(errors).length > 0) {
      return
    }
    
    mutation.mutate(formData)
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Record Payment" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <DoctorSearchSelect
            label="Doctor / Client *"
            value={formData.doctorId}
            onChange={(doctorId) => setFormData({ ...formData, doctorId, selectedInvoiceIds: [] })}
            onBlur={() => setTouched({ ...touched, doctorId: true })}
            error={showError('doctorId') ? errors.doctorId : undefined}
            required
          />
        </div>

        {formData.doctorId && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Invoices * <span className="text-gray-500 font-normal">(multi-select)</span>
              </label>
              {invoices && invoices.length > 0 && (
                <button
                  type="button"
                  onClick={selectAllInvoices}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Select All ({invoices.length})
                </button>
              )}
            </div>
            
            {invoices && invoices.length > 0 ? (
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-48 overflow-y-auto">
                {invoices.map((invoice) => (
                  <label
                    key={invoice.id}
                    className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b last:border-b-0 border-gray-200 dark:border-gray-600 ${
                      formData.selectedInvoiceIds.includes(invoice.id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.selectedInvoiceIds.includes(invoice.id)}
                      onChange={() => toggleInvoiceSelection(invoice.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {invoice.displayId}
                        </span>
                        <span className="text-sm font-semibold text-red-600">
                          {formatCurrency(invoice.balanceAmount)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Issued: {formatDate(invoice.issueDate)} • Total: {formatCurrency(invoice.totalAmount)}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  No unpaid invoices found for this doctor.
                </p>
              </div>
            )}
            
            {showError('selectedInvoiceIds') && (
              <p className="text-sm text-red-500 mt-1">
                <i className="fas fa-exclamation-circle mr-1"></i>
                {errors.selectedInvoiceIds}
              </p>
            )}

            {/* Selection Summary */}
            {selectedInvoices.length > 0 && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    Selected: {selectedInvoices.length} invoice{selectedInvoices.length > 1 ? 's' : ''}
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Total Outstanding: {formatCurrency(totalOutstanding)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Amount (₹) *
              </label>
              {totalOutstanding > 0 && (
                <button
                  type="button"
                  onClick={autoFillAmount}
                  className="text-xs text-primary-600 hover:text-primary-700"
                >
                  Fill {formatCurrency(totalOutstanding)}
                </button>
              )}
            </div>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              onBlur={() => setTouched({ ...touched, amount: true })}
              error={showError('amount') ? errors.amount : undefined}
              required
            />
          </div>
          <SearchableSelect
            label="Method *"
            value={formData.method}
            onChange={(val) => setFormData({ ...formData, method: val as PaymentMethod })}
            searchable={false}
            options={[
              { value: 'cash', label: 'Cash' },
              { value: 'upi', label: 'UPI' },
              { value: 'bank_transfer', label: 'Bank Transfer' },
              { value: 'cheque', label: 'Cheque' },
              { value: 'card', label: 'Card' },
            ]}
          />
        </div>

        {/* Allocation Preview */}
        {paymentAmount > 0 && selectedInvoices.length > 0 && (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
              <i className="fas fa-calculator mr-2"></i>
              Payment Allocation Preview
            </p>
            <div className="text-sm space-y-1">
              {selectedInvoices.map((inv, idx) => {
                // Calculate allocation (oldest first simulation)
                let remainingForCalc = paymentAmount
                let allocatedBefore = 0
                for (let i = 0; i < idx; i++) {
                  const prevInv = selectedInvoices[i]
                  allocatedBefore += Math.min(remainingForCalc, prevInv.balanceAmount)
                  remainingForCalc = Math.max(0, paymentAmount - allocatedBefore)
                }
                const thisAllocation = Math.min(remainingForCalc, inv.balanceAmount)
                
                return (
                  <div key={inv.id} className="flex justify-between text-gray-700 dark:text-gray-300">
                    <span>{inv.displayId}</span>
                    <span className={thisAllocation > 0 ? 'text-green-600' : 'text-gray-400'}>
                      {thisAllocation > 0 ? formatCurrency(thisAllocation) : 'No allocation'}
                    </span>
                  </div>
                )
              })}
              {excessAmount > 0 && (
                <div className="flex justify-between pt-2 border-t border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 font-medium">
                  <span>
                    <i className="fas fa-piggy-bank mr-1"></i>
                    Credit / Advance
                  </span>
                  <span>{formatCurrency(excessAmount)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Reference / Transaction ID"
            value={formData.referenceNumber}
            onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
            placeholder="UPI ID, Cheque #, etc."
          />
          <Input
            label="Received Date *"
            type="date"
            value={formData.receivedAt}
            onChange={(e) => setFormData({ ...formData, receivedAt: e.target.value })}
            onBlur={() => setTouched({ ...touched, receivedAt: true })}
            error={showError('receivedAt') ? errors.receivedAt : undefined}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        {submitAttempted && Object.keys(errors).length > 0 && (
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              <i className="fas fa-exclamation-circle mr-2"></i>
              Please fill in all required fields marked with *.
            </p>
          </div>
        )}

        {mutation.isError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">
              <i className="fas fa-times-circle mr-2"></i>
              Failed to record payment. Please try again.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            <i className="fas fa-check mr-2"></i>
            Record Payment
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// Edit Payment Modal Component
interface EditPaymentModalProps {
  payment: Payment
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

function EditPaymentModal({ payment, isOpen, onClose, onSuccess }: EditPaymentModalProps) {
  const [formData, setFormData] = useState({
    amount: String(payment.amount),
    method: payment.method,
    referenceNumber: payment.referenceNumber || '',
    notes: payment.notes || '',
  })
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return api.put(`/payments/${payment.id}`, {
        amount: parseFloat(data.amount),
        method: data.method,
        referenceNumber: data.referenceNumber || null,
        notes: data.notes || null,
      })
    },
    onSuccess: () => {
      toast.success('Payment updated successfully! Invoice balance has been recalculated.')
      onSuccess()
    },
    onError: () => {
      toast.error('Failed to update payment. Please try again.')
    },
  })

  // Validation
  const errors: Record<string, string> = {}
  if (!formData.amount || parseFloat(formData.amount) <= 0) {
    errors.amount = 'Valid amount is required'
  }

  const showError = (field: string) => (submitAttempted || touched[field]) && errors[field]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitAttempted(true)

    if (Object.keys(errors).length > 0) {
      return
    }

    mutation.mutate(formData)
  }

  const handleClose = () => {
    setFormData({
      amount: String(payment.amount),
      method: payment.method,
      referenceNumber: payment.referenceNumber || '',
      notes: payment.notes || '',
    })
    setTouched({})
    setSubmitAttempted(false)
    onClose()
  }

  // Calculate the difference for preview
  const originalAmount = payment.amount
  const newAmount = parseFloat(formData.amount) || 0
  const difference = newAmount - originalAmount

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Payment" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Payment Info Display */}
        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Receipt #:</span>
            <span className="font-medium text-gray-900 dark:text-white">{payment.displayId}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Doctor:</span>
            <span className="font-medium text-gray-900 dark:text-white">{payment.doctor?.name ?? '-'}</span>
          </div>
          {payment.invoice && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Invoice:</span>
              <span className="font-medium text-primary-600">{payment.invoice.displayId}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Received On:</span>
            <span className="font-medium text-gray-900 dark:text-white">{formatDate(payment.receivedAt)}</span>
          </div>
        </div>

        {/* Amount Field */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Amount (₹) *
            </label>
            <span className="text-xs text-gray-500">
              Original: {formatCurrency(originalAmount)}
            </span>
          </div>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            onBlur={() => setTouched({ ...touched, amount: true })}
            error={showError('amount') ? errors.amount : undefined}
            required
          />
          {/* Show difference preview */}
          {difference !== 0 && (
            <p className={`text-xs mt-1 ${difference > 0 ? 'text-red-600' : 'text-green-600'}`}>
              <i className={`fas fa-arrow-${difference > 0 ? 'up' : 'down'} mr-1`}></i>
              {difference > 0 ? '+' : ''}{formatCurrency(difference)} change
              {payment.invoice && ' — Invoice balance will be updated'}
            </p>
          )}
        </div>

        {/* Method Field */}
        <SearchableSelect
          label="Payment Method *"
          value={formData.method}
          onChange={(val) => setFormData({ ...formData, method: val as PaymentMethod })}
          searchable={false}
          options={[
            { value: 'cash', label: 'Cash' },
            { value: 'upi', label: 'UPI' },
            { value: 'bank_transfer', label: 'Bank Transfer' },
            { value: 'cheque', label: 'Cheque' },
            { value: 'card', label: 'Card' },
          ]}
        />

        {/* Reference Number */}
        <Input
          label="Reference / Transaction ID"
          value={formData.referenceNumber}
          onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
          placeholder="UPI ID, Cheque #, etc."
        />

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        {/* Warning about propagation */}
        {payment.invoice && difference !== 0 && (
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              <i className="fas fa-info-circle mr-2"></i>
              Updating this payment will automatically recalculate the invoice balance and update the invoice status accordingly.
            </p>
          </div>
        )}

        {submitAttempted && Object.keys(errors).length > 0 && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">
              <i className="fas fa-exclamation-circle mr-2"></i>
              Please correct the errors above.
            </p>
          </div>
        )}

        {mutation.isError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">
              <i className="fas fa-times-circle mr-2"></i>
              Failed to update payment. Please try again.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            <i className="fas fa-save mr-2"></i>
            Update Payment
          </Button>
        </div>
      </form>
    </Modal>
  )
}
